import { useEffect, useRef } from 'react';

import { PartnerLookupModal, PartnerLookupModalHandle, PartnerLookupModalShowOptions } from 'components/partner_lookup';
import type { AppFeature, AppUser, HighestRole, Language, SelectedPartner, UserPartner } from 'apis/AppApi';
import { SupportPillWidget } from 'components/SupportPillWidget/SupportPillWidget';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { LocalizedIntlProvider } from 'i18n/LocalizedIntlProvider';
import { AppRouter, isUnauthenticatedRoutePath } from 'app/router';
import { NotificationContextProvider } from 'app/notifications';
import { ErrorBoundary } from 'components/error_boundary';
import { NavigationWrapper } from 'components/navigation';
import { ModalManager } from 'components/modal_manager';
import { AuthPublicApi } from 'apis/AuthPublicApi';
import type { Partner } from 'apis/PartnerApi';
import { getAppIntl } from 'i18n/appIntl';
import { AppContext } from 'app/context';
import { Alert } from 'react-bootstrap';
import { AppApi, AuthApi } from 'apis';
import { useSuperState } from 'hooks';

export type AppFeatures = Record<string, AppFeature>;

// Only allow same-site, root-relative return URLs to avoid open-redirects.
const safeReturnUrl = (url: string | null | undefined): string | null => {

    if (!url) return null;

    // Backslashes can be normalized to forward slashes by URL parsers, turning
    // values like "/\evil.com" into an off-origin redirect. Reject them outright.
    if (url.includes('\\')) return null;

    let parsed: URL;
    try {

        parsed = new URL(url, window.location.origin);

    } catch {

        return null;

    }

    if (parsed.origin !== window.location.origin) return null;
    if (!parsed.pathname.startsWith('/')) return null;

    return parsed.pathname + parsed.search + parsed.hash;

};

// Resolve where to land after authentication. Never returns an auth route
// (e.g. /login), which would render a 404 inside the authenticated shell.
const postAuthTarget = (returnUrl: string | null | undefined): string => {

    const safe = safeReturnUrl(returnUrl);
    if (!safe) return '/';

    const pathname = new URL(safe, window.location.origin).pathname;
    if (isUnauthenticatedRoutePath(pathname)) return '/';

    return safe;

};

export type AppContextValue = {
    allowedItems: string[];
    cultureId: number;
    cultureTag: string;
    currentUser: AppUser;
    features: AppFeatures;
    highestRole: HighestRole;
    isAuthenticated: boolean;
    isFeatureAllowed: (name: string) => boolean;
    isFeatureEnabled: (name: string) => boolean;
    isSystemMode: boolean;
    languages: Language[];
    launchPartnerLookupModal: (options?: PartnerLookupModalShowOptions) => Promise<Partner | null>;
    logOut: () => Promise<void>;
    onLoginSuccess: () => Promise<void>;
    pendingOrderCount: number;
    refreshAppBasics: () => Promise<void>
    selectedPartner: SelectedPartner;
    sideNavOpen: boolean;
    supportPillWidgetCollapsed: boolean;
    userId: number;
    userPartners: UserPartner[];
};

export const App = () => {

    const [state, setState] = useSuperState({
        allowedItems: [] as string[],
        cultureId: null as number,
        cultureTag: 'en-US' as string,
        currentUser: null as AppUser,
        error: null as string,
        features: null as AppFeatures,
        highestRole: null as HighestRole,
        languages: [] as Language[],
        loading: true,
        pendingOrderCount: 0,
        selectedPartner: null as SelectedPartner,
        sideNavOpen: true,
        supportPillWidgetCollapsed: false,
        userId: null as number,
        userPartners: [] as UserPartner[]
    });

    const { allowedItems, cultureId, cultureTag, currentUser, error, features, highestRole, languages, loading,
        pendingOrderCount, selectedPartner, sideNavOpen, supportPillWidgetCollapsed, userId, userPartners } = state;

    const partnerLookupModalRef = useRef<PartnerLookupModalHandle>(null);

    const launchPartnerLookupModal = (options?: PartnerLookupModalShowOptions) => {

        const modal = partnerLookupModalRef?.current;
        if (!modal) return Promise.resolve(null);

        return modal.show(options);

    };

    const fetchAppBasics = async () => {

        const { features, ...appContext } = await AppApi.GetBasics();

        setState({
            ...appContext,
            features: Object.fromEntries(features.map(f => [f.name, f])),
            userId: appContext.currentUser?.userProfileId ?? 0
        });

        return appContext.currentUser as AppUser;

    };

    const fetchInitial = async () => {

        try {

            setState({ loading: true });
            const user = await fetchAppBasics();

            if (user?.needsPasswordReset) {

                window.history.replaceState({}, '', '/account#password');

            }

            setState({ loading: false });

        } catch (e){

            const { formatMessage } = getAppIntl();
            setState({
                error: e?.Message || e?.message || formatMessage({ id: 'failed_to_load_app_context.f35400ff', defaultMessage: 'Failed to load app context' }),
                loading: false
            });

        }

    };

    // Ring membership only ("use this to show options"). Use for gating the display of dev/admin
    // affordances that the backend authorizes on allowed-only (e.g. CheckFeatureFlag), independent
    // of the user's early-access opt-in preference.
    const isFeatureAllowed = (name: string) => {

        const feature = state?.features?.[name];
        return !!feature && feature.isAllowed;

    };

    const isFeatureEnabled = (name: string) => {

        const feature = state?.features?.[name];
        return !!feature && feature.isAllowed && feature.isEnabled;

    };

    const logOut = async () => {

        try {

            setState({ loading: true });
            await AuthApi.Logout();

            // If we successfully logged out, reload to the login page
            window.location.href = '/';

        } catch (e){

            console.error('Failed to log out:', e);
            setState({ loading: false });

        }

    };

    // Loads the full app context (GetBasics) and flips the shell to the
    // authenticated view. Shared by the initial bootstrap and post-login handoff.
    const onLoginSuccess = async () => {

        const target = postAuthTarget(new URLSearchParams(window.location.search).get('ReturnUrl'));

        // Update the address bar without a reload so the authenticated router
        // mounts at the return URL rather than /login.
        window.history.replaceState({}, '', target);

        await fetchInitial();

    };

    const initialize = async () => {

        try {

            await AuthPublicApi.GetAntiForgeryToken();

        } catch (e: any) {

            const { formatMessage } = getAppIntl();
            setState({
                error: e?.Message || e?.message || formatMessage({ id: 'failed_to_initialize_a_secure_session_pl.4468e289', defaultMessage: 'Failed to initialize a secure session. Please refresh and try again.' }),
                loading: false
            });
            return;

        }

        let me;
        try {

            me = await AuthPublicApi.Me();

        } catch (e: any) {

            const { formatMessage } = getAppIntl();
            setState({
                error: e?.Message || e?.message || formatMessage({ id: 'failed_to_load_sign_in_resources.941b375d', defaultMessage: 'Failed to load sign-in resources.' }),
                loading: false
            });
            return;

        }

        const isAuthed = !!me?.currentUser?.userId;
        const onUnauthPath = isUnauthenticatedRoutePath(window.location.pathname);

        if (!isAuthed) {

            // Anonymous user on a protected path: send them to the sign-in page,
            // preserving where they were trying to go.
            if (!onUnauthPath) {

                const returnTarget = encodeURIComponent(window.location.pathname + window.location.search);
                window.history.replaceState({}, '', `/login?ReturnUrl=${returnTarget}`);

            }

            setState({ loading: false });
            return;

        }

        // Authenticated but sitting on an auth route (e.g. /login): bounce to the
        // return URL (or home) before loading the dashboard.
        if (onUnauthPath) {

            const target = postAuthTarget(new URLSearchParams(window.location.search).get('ReturnUrl'));
            window.history.replaceState({}, '', target);

        }

        await fetchInitial();

    };

    useEffect(() => {

        initialize();

    }, []);

    const context: AppContextValue = {
        allowedItems,
        cultureId,
        cultureTag,
        currentUser,
        features,
        highestRole,
        isAuthenticated: !!currentUser,
        isFeatureAllowed,
        isFeatureEnabled,
        isSystemMode: selectedPartner?.type === 1,
        languages,
        launchPartnerLookupModal,
        logOut,
        onLoginSuccess,
        pendingOrderCount,
        refreshAppBasics: fetchAppBasics,
        selectedPartner,
        sideNavOpen,
        supportPillWidgetCollapsed,
        userId,
        userPartners
    };

    return (
        <AppContext.Provider value={context}>
            <LocalizedIntlProvider cultureTag={cultureTag}>
                <NotificationContextProvider>
                    {loading ?
                        (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: 'calc(100vh - 70px)' }}>
                                <GMetrixLoader/>
                            </div>
                        )
                        : error
                            ?
                            (
                                <Alert show={!!error} variant={'danger'}>
                                    {error}
                                </Alert>
                            )
                            :
                            (
                                <>
                                    <SupportPillWidget />
                                    <ErrorBoundary>
                                        <NavigationWrapper>
                                            <ModalManager>

                                                <AppRouter />

                                                {!!currentUser && (
                                                    <PartnerLookupModal ref={partnerLookupModalRef} />
                                                )}

                                            </ModalManager>
                                        </NavigationWrapper>
                                    </ErrorBoundary>
                                </>
                            )
                    }
                </NotificationContextProvider>
            </LocalizedIntlProvider>
        </AppContext.Provider>
    );

};
